import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { money, number, thaiDate, thaiDateTime } from '../lib/format'

/**
 * Product royalties (part 138).
 *
 * A rate is basis points of gross: 12.5% is 1250, so the whole range is
 * 0..5000. The browser computes the preview with BigInt using exactly the same
 * half-up rule and floor/cap as royalty.Calc on the server, so the figure on
 * screen and the figure stored in a run can never drift.
 */

interface RoyaltyRate {
  id: string
  product_code: string
  rate_bp: number
  min_satang: number
  max_satang: number
  effective_on: string
  created_at: string
}

interface RoyaltyRun {
  id: string
  period: string
  product_code: string
  gross_satang: number
  rate_bp: number
  royalty_satang: number
  created_at: string
}

interface ProductTotal {
  product_code: string
  runs: number
  gross_satang: number
  royalty_satang: number
}

interface PeriodTotals {
  runs: number
  gross_satang: number
  royalty_satang: number
  by_product: ProductTotal[]
}

const RATE_KEY = ['royalty-rates']

function isoDay(offsetDays = 0): string {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function isoMonth(offsetMonths = 0): string {
  const d = new Date()
  d.setDate(1)
  d.setMonth(d.getMonth() + offsetMonths)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** "1,000.00" -> 100000n satang. Returns null for anything but baht with <= 2 dp. */
function parseSatang(raw: string): bigint | null {
  const s = raw.trim().replace(/,/g, '')
  if (!/^\d+(\.\d{0,2})?$/.test(s)) return null
  const [whole, frac = ''] = s.split('.')
  return BigInt(whole) * 100n + BigInt((frac + '00').slice(0, 2))
}

/** "12.5" -> 1250 bp. Exact string math: the rate never passes through a float. */
function parsePercentBP(raw: string): number | null {
  const s = raw.trim().replace(/,/g, '')
  if (!/^\d+(\.\d{0,2})?$/.test(s)) return null
  const [whole, frac = ''] = s.split('.')
  const bp = Number(whole) * 100 + Number((frac + '00').slice(0, 2))
  if (!Number.isFinite(bp) || bp < 0 || bp > 5000) return null
  return bp
}

/** 1250 -> "12.5%", 500 -> "5%", 505 -> "5.05%". */
function percentLabel(bp: number): string {
  const whole = Math.trunc(bp / 100)
  const frac = bp % 100
  if (frac === 0) return `${whole}%`
  if (frac % 10 === 0) return `${whole}.${frac / 10}%`
  return `${whole}.${String(frac).padStart(2, '0')}%`
}

function bahtLabel(satang: bigint): string {
  if (satang > BigInt(Number.MAX_SAFE_INTEGER)) return `${satang.toString()} สตางค์`
  return money(Number(satang))
}

function numberLabel(satang: number): string {
  if (Math.abs(satang) > Number.MAX_SAFE_INTEGER) return `${satang} สตางค์`
  return money(satang)
}

/** The newest card for a product effective on or before a day. */
function resolveRate(rates: RoyaltyRate[], product: string, on: string): RoyaltyRate | null {
  let best: RoyaltyRate | null = null
  for (const r of rates) {
    if (r.product_code !== product) continue
    if (r.effective_on > on) continue
    if (!best || r.effective_on > best.effective_on) best = r
  }
  return best
}

/** Mirrors royalty.Calc: half-up, then floor, then cap (max 0 = no cap). */
function calcRoyalty(gross: bigint, rateBP: number, min: bigint, max: bigint): bigint {
  let out = (gross * BigInt(rateBP) + 5000n) / 10000n
  if (min > 0n && out < min) out = min
  if (max > 0n && out > max) out = max
  return out < 0n ? 0n : out
}

export function Royalties() {
  const { me } = useAuth()
  const canManage = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager'
  const qc = useQueryClient()
  const today = isoDay()

  const [period, setPeriod] = useState(isoMonth())

  const ratesQ = useQuery({
    queryKey: RATE_KEY,
    queryFn: () => list<RoyaltyRate>('/v1/royalty/rates'),
    staleTime: 30_000,
  })
  const runsQ = useQuery({
    queryKey: ['royalty-runs', period],
    queryFn: () => list<RoyaltyRun>(`/v1/royalty/runs?period=${period}`),
    enabled: period.length === 7,
  })
  const totalsQ = useQuery({
    queryKey: ['royalty-summary', period],
    queryFn: () => api<PeriodTotals>(`/v1/royalty/summary?period=${period}`),
    enabled: period.length === 7,
  })

  const rates = ratesQ.data ?? []
  const products = useMemo(
    () => Array.from(new Set(rates.map((r) => r.product_code))).sort(),
    [rates],
  )

  // ------------------------------------------------------------ rate form
  const [product, setProduct] = useState('SKU-001')
  const [percent, setPercent] = useState('10')
  const [minBaht, setMinBaht] = useState('0.00')
  const [maxBaht, setMaxBaht] = useState('0.00')
  const [effectiveOn, setEffectiveOn] = useState(today)

  const rateBP = parsePercentBP(percent)
  const minSatang = parseSatang(minBaht)
  const maxSatang = parseSatang(maxBaht)
  const rateValid =
    product.trim() !== '' &&
    rateBP !== null &&
    minSatang !== null &&
    maxSatang !== null &&
    minSatang <= 1_000_000_000_000_000n &&
    maxSatang <= 1_000_000_000_000_000n &&
    (maxSatang === 0n || maxSatang >= minSatang)

  const saveRate = useMutation({
    mutationFn: () =>
      apiWrite<RoyaltyRate>('/v1/royalty/rates', 'POST', {
        product_code: product.trim().toUpperCase(),
        rate_bp: rateBP,
        min_satang: minSatang === null ? 0 : Number(minSatang),
        max_satang: maxSatang === null ? 0 : Number(maxSatang),
        effective_on: effectiveOn,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: RATE_KEY }),
  })

  // ------------------------------------------------------------ calculator
  const [calcProduct, setCalcProduct] = useState('')
  const [calcGross, setCalcGross] = useState('100,000.00')
  const activeProduct = calcProduct || products[0] || ''
  const activeRate = resolveRate(rates, activeProduct, today)
  const calcGrossSatang = parseSatang(calcGross)
  const calc =
    activeRate && calcGrossSatang !== null && calcGrossSatang >= 0n
      ? (() => {
          const min = BigInt(activeRate.min_satang)
          const max = BigInt(activeRate.max_satang)
          const raw = (calcGrossSatang * BigInt(activeRate.rate_bp) + 5000n) / 10000n
          const final = calcRoyalty(calcGrossSatang, activeRate.rate_bp, min, max)
          const clamp =
            final === raw
              ? 'ไม่มีการปรับ'
              : min > 0n && raw < min
                ? 'ปรับขึ้นตามขั้นต่ำ'
                : max > 0n && raw > max
                  ? 'ปรับลงตามเพดาน'
                  : 'ไม่มีการปรับ'
          return { raw, final, clamp, min, max }
        })()
      : null

  // ----------------------------------------------------------------- run
  const [runPeriod, setRunPeriod] = useState(isoMonth())
  const [runProduct, setRunProduct] = useState('')
  const [runGross, setRunGross] = useState('100,000.00')
  const runProductCode = runProduct || products[0] || ''
  const runGrossSatang = parseSatang(runGross)
  const runReady = runProductCode !== '' && runPeriod.length === 7 && runGrossSatang !== null

  const run = useMutation({
    mutationFn: () =>
      apiWrite<RoyaltyRun>('/v1/royalty/runs', 'POST', {
        period: runPeriod,
        product_code: runProductCode,
        gross_satang: runGrossSatang === null ? 0 : Number(runGrossSatang),
      }),
    onSuccess: (data) => {
      setPeriod(data.period)
      void qc.invalidateQueries({ queryKey: ['royalty-runs'] })
      void qc.invalidateQueries({ queryKey: ['royalty-summary'] })
    },
  })

  const runs = runsQ.data ?? []
  const totals = totalsQ.data

  return (
    <>
      <PageHead
        title="ค่าลิขสิทธิ์"
        sub="อัตราต่อสินค้า · ขั้นต่ำและเพดาน · คำนวณเป็นสตางค์จำนวนเต็ม"
        actions={<span className="chip">{number(rates.length)} อัตราที่บันทึกไว้</span>}
      />

      <div className="grid-main">
        <div className="card card-pad">
          <div className="row">
            <span className="card-label">ตารางอัตราค่าลิขสิทธิ์</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>เรียงตามสินค้าและวันที่มีผลใหม่สุด</span>
          </div>
          {ratesQ.isLoading ? (
            <div style={{ marginTop: 14 }}><Skel h={180} /></div>
          ) : ratesQ.isError ? (
            <div className="form-error" style={{ marginTop: 14 }}>โหลดอัตราค่าลิขสิทธิ์ไม่สำเร็จ — ลองรีเฟรชหน้าอีกครั้ง</div>
          ) : rates.length === 0 ? (
            <div style={{ marginTop: 14 }}>
              <Empty
                title="ยังไม่มีอัตราค่าลิขสิทธิ์"
                body="บันทึกอัตราของสินค้าก่อน แล้วจึงคำนวณค่าลิขสิทธิ์รายงวดได้"
              />
            </div>
          ) : (
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="data">
                <thead>
                  <tr>
                    <th>สินค้า</th>
                    <th>อัตรา</th>
                    <th>ขั้นต่ำ</th>
                    <th>เพดาน</th>
                    <th>มีผลตั้งแต่</th>
                  </tr>
                </thead>
                <tbody>
                  {rates.map((r) => (
                    <tr key={r.id} style={{ cursor: 'default' }}>
                      <td className="mono" style={{ whiteSpace: 'nowrap' }}>{r.product_code}</td>
                      <td className="num tnum" style={{ whiteSpace: 'nowrap' }}>{percentLabel(r.rate_bp)}</td>
                      <td className="num tnum" style={{ whiteSpace: 'nowrap' }}>
                        {r.min_satang > 0 ? numberLabel(r.min_satang) : <span className="dim">—</span>}
                      </td>
                      <td className="num tnum" style={{ whiteSpace: 'nowrap' }}>
                        {r.max_satang > 0 ? numberLabel(r.max_satang) : <span className="dim">ไม่จำกัด</span>}
                      </td>
                      <td className="dim" style={{ whiteSpace: 'nowrap' }}>{thaiDate(r.effective_on)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-label">บันทึกอัตราใหม่</div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.8 }}>
            อัตราเป็นเปอร์เซ็นต์ไม่เกิน 2 ตำแหน่ง ระบบเก็บเป็นจำนวนเต็มฐานร้อย (basis points)
            เว้นเพดานเป็น 0 หมายถึงไม่จำกัด
          </div>
          <form
            style={{ marginTop: 12 }}
            onSubmit={(e) => { e.preventDefault(); if (canManage && rateValid) saveRate.mutate() }}
          >
            <div className="field">
              <label htmlFor="ry-product">รหัสสินค้า</label>
              <input id="ry-product" className="input mono" placeholder="SKU-001" value={product}
                disabled={!canManage} onChange={(e) => setProduct(e.target.value)} />
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="ry-percent">อัตรา (%)</label>
                <input id="ry-percent" className="input mono" inputMode="decimal" placeholder="10"
                  value={percent} disabled={!canManage} onChange={(e) => setPercent(e.target.value)} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="ry-effective">มีผลตั้งแต่</label>
                <input id="ry-effective" className="input" type="date" value={effectiveOn}
                  disabled={!canManage} onChange={(e) => setEffectiveOn(e.target.value)} />
              </div>
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="ry-min">ขั้นต่ำ (บาท)</label>
                <input id="ry-min" className="input mono" inputMode="decimal" placeholder="0.00"
                  value={minBaht} disabled={!canManage} onChange={(e) => setMinBaht(e.target.value)} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="ry-max">เพดาน (บาท)</label>
                <input id="ry-max" className="input mono" inputMode="decimal" placeholder="0.00"
                  value={maxBaht} disabled={!canManage} onChange={(e) => setMaxBaht(e.target.value)} />
              </div>
            </div>
            <button className="btn btn-primary" type="submit" disabled={!canManage || !rateValid || saveRate.isPending}
              style={{ width: '100%' }}>
              {saveRate.isPending ? 'กำลังบันทึก…' : 'บันทึกอัตรา'}
            </button>
          </form>
          {!canManage ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
              เฉพาะเจ้าของ ผู้ดูแลระบบ และผู้จัดการตรวจสอบเท่านั้นที่แก้อัตราได้
            </div>
          ) : null}
          {percent !== '' && parsePercentBP(percent) === null ? (
            <div className="form-error" style={{ marginTop: 10 }}>อัตราต้องอยู่ระหว่าง 0% ถึง 50% และมีทศนิยมไม่เกิน 2 ตำแหน่ง</div>
          ) : null}
          {minSatang !== null && maxSatang !== null && maxSatang > 0n && maxSatang < minSatang ? (
            <div className="form-error" style={{ marginTop: 10 }}>เพดานต้องไม่น้อยกว่าขั้นต่ำ</div>
          ) : null}
          {saveRate.isError ? (
            <div className="form-error" style={{ marginTop: 10 }}>
              {saveRate.error instanceof Error ? saveRate.error.message : 'บันทึกอัตราไม่สำเร็จ'}
            </div>
          ) : null}
          {saveRate.isSuccess ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
              บันทึกแล้ว · {saveRate.data.product_code} {percentLabel(saveRate.data.rate_bp)} มีผล {thaiDate(saveRate.data.effective_on)}
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid-main" style={{ marginTop: 16 }}>
        <div className="card card-pad">
          <div className="card-label">เครื่องคำนวณค่าลิขสิทธิ์</div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
            ใช้อัตราที่มีผล ณ วันนี้ · ปัดครึ่งขึ้นแล้วบังคับขั้นต่ำและเพดานตามสัญญา
          </div>
          <div className="row" style={{ gap: 10, marginTop: 14 }}>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="ry-calc-product">สินค้า</label>
              <select id="ry-calc-product" className="input" value={activeProduct}
                onChange={(e) => setCalcProduct(e.target.value)} disabled={products.length === 0}>
                {products.length === 0 ? <option value="">ยังไม่มีสินค้า</option> : null}
                {products.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="ry-calc-gross">ยอดขายรวม (บาท)</label>
              <input id="ry-calc-gross" className="input mono" inputMode="decimal" placeholder="100,000.00"
                value={calcGross} onChange={(e) => setCalcGross(e.target.value)} />
            </div>
          </div>

          {!activeRate ? (
            <div className="dim" style={{ fontSize: 12.5, marginTop: 14, lineHeight: 1.9 }}>
              ยังไม่มีอัตราของสินค้านี้ที่มีผลถึงวันนี้ — บันทึกอัตราทางด้านบนก่อน
            </div>
          ) : calc ? (
            <>
              <div className="row" style={{ marginTop: 4, fontSize: 12 }}>
                <span className="chip gold">อัตรา {percentLabel(activeRate.rate_bp)}</span>
                <span className="dim" style={{ marginLeft: 'auto' }}>มีผล {thaiDate(activeRate.effective_on)}</span>
              </div>
              <div className="rule" style={{ margin: '14px 0' }} />
              <div className="row" style={{ alignItems: 'baseline' }}>
                <span className="dim" style={{ fontSize: 12 }}>ค่าลิขสิทธิ์ที่ต้องจ่าย</span>
                <span className="display-m tnum" style={{ marginLeft: 'auto' }}>{bahtLabel(calc.final)}</span>
              </div>
              <dl className="kv" style={{ marginTop: 12 }}>
                <dt>ก่อนปรับ</dt>
                <dd className="tnum" style={{ textAlign: 'right' }}>{bahtLabel(calc.raw)}</dd>
                <dt>ขั้นต่ำ</dt>
                <dd className="tnum" style={{ textAlign: 'right' }}>
                  {calc.min > 0n ? bahtLabel(calc.min) : 'ไม่มี'}
                </dd>
                <dt>เพดาน</dt>
                <dd className="tnum" style={{ textAlign: 'right' }}>
                  {calc.max > 0n ? bahtLabel(calc.max) : 'ไม่จำกัด'}
                </dd>
                <dt>ผลการปรับ</dt>
                <dd style={{ textAlign: 'right' }}>{calc.clamp}</dd>
                <dt>จำนวนเต็ม</dt>
                <dd className="mono" style={{ textAlign: 'right' }}>{calc.final.toString()} สตางค์</dd>
              </dl>
            </>
          ) : (
            <div className="form-error" style={{ marginTop: 12 }}>ยอดขายรวมไม่ถูกต้อง — ใช้ตัวเลขไม่เกิน 2 ตำแหน่ง</div>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-label">คำนวณค่าลิขสิทธิ์รายงวด</div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.8 }}>
            ระบบเลือกอัตราที่มีผล ณ วันสิ้นงวด แล้วบันทึกผลไว้ · คำนวณงวดเดิมซ้ำจะทับรายการเดิม ไม่สร้างรายการซ้ำ
          </div>
          <form
            style={{ marginTop: 12 }}
            onSubmit={(e) => { e.preventDefault(); if (canManage && runReady) run.mutate() }}
          >
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="ry-run-period">งวด</label>
                <input id="ry-run-period" className="input" type="month" value={runPeriod}
                  disabled={!canManage} onChange={(e) => setRunPeriod(e.target.value)} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="ry-run-product">สินค้า</label>
                <select id="ry-run-product" className="input" value={runProductCode} disabled={!canManage}
                  onChange={(e) => setRunProduct(e.target.value)}>
                  {products.length === 0 ? <option value="">ยังไม่มีสินค้า</option> : null}
                  {products.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div className="field">
              <label htmlFor="ry-run-gross">ยอดขายรวมในงวด (บาท)</label>
              <input id="ry-run-gross" className="input mono" inputMode="decimal" placeholder="100,000.00"
                value={runGross} disabled={!canManage} onChange={(e) => setRunGross(e.target.value)} />
            </div>
            <button className="btn btn-primary" type="submit"
              disabled={!canManage || !runReady || run.isPending} style={{ width: '100%' }}>
              {run.isPending ? 'กำลังคำนวณ…' : 'คำนวณและบันทึกค่าลิขสิทธิ์'}
            </button>
          </form>
          {!canManage ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
              เฉพาะเจ้าของ ผู้ดูแลระบบ และผู้จัดการตรวจสอบเท่านั้นที่สร้างรายการได้
            </div>
          ) : null}
          {run.isError ? (
            <div className="form-error" style={{ marginTop: 10 }}>
              {run.error instanceof Error ? run.error.message : 'คำนวณค่าลิขสิทธิ์ไม่สำเร็จ'}
            </div>
          ) : null}
          {run.isSuccess ? (
            <div style={{ marginTop: 12 }}>
              <div className="row">
                <span className="chip gold">คำนวณแล้ว</span>
                <span className="mono" style={{ marginLeft: 'auto', fontSize: 12 }}>{run.data.period} · {run.data.product_code}</span>
              </div>
              <dl className="kv" style={{ marginTop: 10 }}>
                <dt>ยอดขายรวม</dt>
                <dd style={{ textAlign: 'right' }}>{numberLabel(run.data.gross_satang)}</dd>
                <dt>อัตราที่ใช้</dt>
                <dd className="mono" style={{ textAlign: 'right' }}>{percentLabel(run.data.rate_bp)}</dd>
                <dt>ค่าลิขสิทธิ์</dt>
                <dd className="tnum" style={{ textAlign: 'right' }}>{numberLabel(run.data.royalty_satang)}</dd>
              </dl>
            </div>
          ) : null}
        </div>
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="row">
          <span className="card-label">ประวัติการคำนวณ</span>
          <div className="field" style={{ marginLeft: 'auto', marginBottom: 0, width: 170 }}>
            <input className="input" type="month" aria-label="งวดที่แสดง" value={period}
              onChange={(e) => setPeriod(e.target.value)} />
          </div>
        </div>

        <div className="grid-3" style={{ marginTop: 14 }}>
          <Stat label="รายการในงวด" value={number(totals?.runs ?? runs.length)} unit="รายการ" />
          <Stat label="ยอดขายรวม" value={numberLabel(totals?.gross_satang ?? 0)} foot="รวมทุกสินค้าในงวด" />
          <Stat label="ค่าลิขสิทธิ์" value={numberLabel(totals?.royalty_satang ?? 0)} foot="หลังบังคับขั้นต่ำและเพดาน" />
        </div>

        {runsQ.isLoading ? (
          <div style={{ marginTop: 14 }}><Skel h={160} /></div>
        ) : runsQ.isError ? (
          <div className="form-error" style={{ marginTop: 14 }}>โหลดประวัติการคำนวณไม่สำเร็จ</div>
        ) : runs.length === 0 ? (
          <div style={{ marginTop: 14 }}>
            <Empty title="ยังไม่มีรายการในงวดนี้" body="เลือกงวดและสินค้า แล้วกดคำนวณค่าลิขสิทธิ์" />
          </div>
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="data">
              <thead>
                <tr>
                  <th>งวด</th>
                  <th>สินค้า</th>
                  <th>ยอดขายรวม</th>
                  <th>อัตรา</th>
                  <th>ค่าลิขสิทธิ์</th>
                  <th>คำนวณเมื่อ</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} style={{ cursor: 'default' }}>
                    <td className="mono" style={{ whiteSpace: 'nowrap' }}>{r.period}</td>
                    <td className="mono" style={{ whiteSpace: 'nowrap' }}>{r.product_code}</td>
                    <td className="num tnum" style={{ whiteSpace: 'nowrap' }}>{numberLabel(r.gross_satang)}</td>
                    <td className="num tnum" style={{ whiteSpace: 'nowrap' }}>{percentLabel(r.rate_bp)}</td>
                    <td className="num tnum" style={{ whiteSpace: 'nowrap' }}>{numberLabel(r.royalty_satang)}</td>
                    <td className="dim" style={{ whiteSpace: 'nowrap' }}>{thaiDateTime(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totals && totals.by_product.length > 0 ? (
          <div className="row" style={{ marginTop: 14, flexWrap: 'wrap', gap: 8 }}>
            {totals.by_product.map((p) => (
              <span key={p.product_code} className="chip">
                {p.product_code} · {number(p.runs)} รายการ · {numberLabel(p.royalty_satang)}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="card-label">หลักการคำนวณ</div>
        <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
          <li>อัตราเก็บเป็นจำนวนเต็มฐานร้อย (basis points) · 12.5% คือ 1250 · ไม่มีทศนิยมลอยตัวในเส้นทางเงิน</li>
          <li>ค่าลิขสิทธิ์ = ยอดขายรวม × อัตรา ÷ 10000 แล้วปัดครึ่งขึ้นเฉพาะสตางค์สุดท้าย</li>
          <li>ขั้นต่ำบังคับยอดขึ้นก่อน แล้วจึงบังคับเพดานลง · เพดาน 0 หมายถึงไม่จำกัด</li>
          <li>อัตราที่ใช้คืองวดล่าสุดที่มีผลไม่เกินวันสิ้นงวดที่คำนวณ</li>
          <li>คำนวณงวดและสินค้าเดิมซ้ำจะทับรายการเดิม เพื่อไม่ให้มีรายการจ่ายซ้ำในเดือนเดียว</li>
        </ul>
      </div>
    </>
  )
}
